import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { UploadField } from '../../components/UploadField';
import { useCurrentReseller, updateReseller } from '../../api/reseller';
import { uploadAsset } from '../../api/uploads';

/** Reseller branding editor with side-by-side live preview. */
export function Branding() {
  const qc = useQueryClient();
  const { data: currentReseller } = useCurrentReseller();
  const [displayName, setDisplayName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#0F766E');
  const [supportEmail, setSupportEmail] = useState('');
  const [supportPhone, setSupportPhone] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  // Optional dark-theme logo. The brand resolver already served `logoDarkUrl` when present
  // but nothing could WRITE it, so a reseller whose logo is dark-on-transparent had no way
  // to stay legible in dark mode. Blank keeps the single logo in both themes.
  const [logoDarkUrl, setLogoDarkUrl] = useState<string | null>(null);
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [showCss, setShowCss] = useState(false);
  const [customCss, setCustomCss] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Raw branding blob carries fields not surfaced as top-level (favicon, custom CSS). */
  const brandingBlob = ((currentReseller as { branding?: Record<string, unknown> } | undefined)?.branding ?? {}) as Record<string, unknown>;

  function resetFromServer() {
    if (!currentReseller) return;
    setDisplayName(currentReseller.displayName);
    setPrimaryColor(currentReseller.primaryColor ?? '#0F766E');
    setSupportEmail(currentReseller.supportEmail ?? '');
    setSupportPhone(currentReseller.supportPhone ?? '');
    setLogoUrl(currentReseller.logoUrl ?? null);
    setLogoDarkUrl((brandingBlob.logoDarkUrl as string) ?? null);
    setFaviconUrl((brandingBlob.faviconUrl as string) ?? null);
    setCustomCss((brandingBlob.customCss as string) ?? '');
    setError(null);
  }

  useEffect(() => {
    resetFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentReseller]);

  if (!currentReseller) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
        Loading branding…
      </div>
    );
  }

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateReseller({
        // Merge over the existing blob so fields this form doesn't manage (e.g. shortName) survive.
        branding: { ...brandingBlob, displayName, primaryColor, supportEmail, supportPhone, logoUrl, logoDarkUrl, faviconUrl, customCss },
      });
      await qc.invalidateQueries({ queryKey: ['reseller', 'me'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch {
      setError('Could not save branding. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Reseller"
        title="Branding"
        subtitle="Customize how your customers see Possignin under your brand."
      />

      <div className="grid lg:grid-cols-2 gap-3 sm:gap-4">
        {/* Form */}
        <Card className="p-5 sm:p-6">
          <CardHeader title="Brand details" />
          <div className="space-y-5">
            <Input
              label="Display name"
              hint="The name shown in the POS UI and emails"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />

            <div className="grid sm:grid-cols-3 gap-5">
              <UploadField
                label="Logo for LIGHT mode"
                hint="Upload your dark/colour logo — it sits on a white page"
                value={logoUrl}
                onUpload={async (f) => setLogoUrl((await uploadAsset(f)).url)}
              />
              <UploadField
                label="Logo for DARK mode"
                hint="Upload your white/light logo. Optional — falls back to the one on the left"
                value={logoDarkUrl}
                onUpload={async (f) => setLogoDarkUrl((await uploadAsset(f)).url)}
              />
              <UploadField
                label="Favicon"
                hint="32×32 PNG or ICO"
                value={faviconUrl}
                onUpload={async (f) => setFaviconUrl((await uploadAsset(f)).url)}
              />
            </div>

            <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
              <Input
                label="Primary color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                hint="Used for buttons, accents, and links"
              />
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="w-12 h-[42px] rounded-md border border-zinc-300 dark:border-zinc-700 cursor-pointer bg-transparent self-end"
              />
            </div>

            <Input
              label="Support email"
              type="email"
              value={supportEmail}
              onChange={(e) => setSupportEmail(e.target.value)}
            />
            <Input label="Support phone" value={supportPhone} onChange={(e) => setSupportPhone(e.target.value)} />

            <div>
              <button
                type="button"
                onClick={() => setShowCss(!showCss)}
                className="text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:text-zinc-950 dark:hover:text-zinc-50"
              >
                Custom CSS {showCss ? '▴' : '▾'}{' '}
                <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400 ml-1">(advanced)</span>
              </button>
              {showCss && (
                <textarea
                  value={customCss}
                  onChange={(e) => setCustomCss(e.target.value)}
                  rows={5}
                  placeholder="/* Custom CSS injected into your customers' POS pages */"
                  className="mt-2 block w-full px-3 py-2.5 text-sm rounded-md font-mono bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm"
                />
              )}
            </div>

            <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700">
              {error && <p className="text-sm text-rose-600 dark:text-rose-400 mb-3 text-right">{error}</p>}
              <div className="flex justify-end items-center gap-3">
                {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
                <Button variant="ghost" onClick={resetFromServer} disabled={saving}>
                  Discard
                </Button>
                <Button onClick={save} loading={saving}>Save changes</Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Live preview */}
        <div className="lg:sticky lg:top-6 self-start">
          {/* "Live" refers to the BRANDING updating as you type — not to live data.
              Named plainly so the sample KPIs below aren't mistaken for real sales. */}
          <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
            Brand preview
          </div>
          <Card
            className="overflow-hidden border-t-4"
            style={{ borderTopColor: primaryColor }}
          >
            {/* Mock app chrome */}
            <div className="px-5 py-3 border-b border-zinc-200 dark:border-zinc-700 flex items-center gap-2">
              {logoUrl ? (
                <img src={logoUrl} alt={displayName} className="h-7 max-w-[120px] object-contain" />
              ) : (
                <span
                  className="w-7 h-7 rounded-md grid place-items-center text-white font-semibold text-xs"
                  style={{ backgroundColor: primaryColor }}
                >
                  {displayName.charAt(0)}
                </span>
              )}
              <span className="font-semibold text-sm text-zinc-950 dark:text-zinc-50">{displayName}</span>
              <span className="ml-auto text-[11px] text-zinc-500 dark:text-zinc-400 mono">
                {currentReseller.slug}.itcitysoft.com
              </span>
            </div>

            <div className="p-5 sm:p-6">
              <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1">
                Home
              </div>
              <h3 className="text-xl font-semibold text-zinc-950 dark:text-zinc-50">Welcome back</h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-1">Here's how your shop is doing today.</p>

              {/* Sample figures — this panel exists to preview BRAND (logo, colour,
                  name), not to report numbers. Labelled so nobody reads them as
                  this reseller's real sales. */}
              <div className="mt-5 grid grid-cols-2 gap-3">
                <PreviewKpi label="Today's sales (sample)" value="৳ 28,500" />
                <PreviewKpi label="Orders (sample)" value="142" />
              </div>

              <div className="mt-5 flex items-center gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-md text-white text-sm font-medium"
                  style={{ backgroundColor: primaryColor }}
                >
                  New sale
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-md text-sm font-medium border"
                  style={{ borderColor: primaryColor, color: primaryColor }}
                >
                  Reports
                </button>
              </div>

              <div className="mt-6 pt-4 border-t border-zinc-200 dark:border-zinc-700 text-[11px] text-zinc-500 dark:text-zinc-400">
                Need help? Reach us at{' '}
                <span className="font-medium text-zinc-700 dark:text-zinc-200">{supportEmail}</span> or{' '}
                <span className="tabular-nums">{supportPhone}</span>.
              </div>
            </div>
          </Card>

          <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
            Approximately how a customer's POS will look when loaded under{' '}
            <span className="mono">{currentReseller.slug}.itcitysoft.com</span>. The figures shown
            are sample data, not your customers' sales.
          </p>
        </div>
      </div>
    </div>
  );
}


function PreviewKpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-zinc-200 dark:border-zinc-600 p-3 bg-zinc-50/50 dark:bg-zinc-900/40">
      <div className="text-[10px] font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className="mt-1 text-base font-semibold text-zinc-950 dark:text-zinc-50 tabular-nums">{value}</div>
    </div>
  );
}
